import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Segmented, Avatar, Button } from 'antd';
import {
  TeamOutlined, LoginOutlined, LogoutOutlined, ClockCircleOutlined,
  EnvironmentOutlined, CheckCircleOutlined, ApartmentOutlined, UserOutlined, EyeOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import SearchBar from '../../components/searchBar';
import ColumnVisibility from '../../components/columnVisibility';
import CommonTable from '../../components/commonTable';
import CustomPagination from '../../components/pagination';
import DateRangeFilter from '../../components/dateRangeFilter';
import StatCard from '../../components/dashboards/StatCard';
import { useGetAttendanceQuery } from '../../services/attendance';
import { UserAttendanceRoute, AttendanceDetailRoute } from '../../routes/routepath';
import { ATTENDANCE_FLOORS as FLOORS } from '../../constants/attendanceFloors';
import '../../components/attendancePage/styles.scss';
import './styles.scss';

/* ─── Types ──────────────────────────────────────────────────────────── */

type VisibleColumns = Record<string, boolean>;

interface Session {
  key:         string;
  attendeeId:  string | null;
  date:        string | null;
  name:        string;
  mobile:      string;
  branch:      string;
  floors:      string[];
  floor:       string;
  checkIn:     string | null;
  checkOut:    string | null;
  durationMin: number | null;
  status:      'inside' | 'left' | 'out';
  sortTime:    number;
}

/* ─── Helpers ────────────────────────────────────────────────────────── */

const initials = (n: string) =>
  n && n !== '—' ? n.trim().split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';
const AV_COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#06b6d4', '#8b5cf6'];
const avatarColor = (n: string) => AV_COLORS[(n?.length || 0) % AV_COLORS.length];

const fmtTime = (t: string | null) => (t ? dayjs(t).format('hh:mm A') : null);
const fmtDur  = (m: number) => (m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

function buildSessions(records: any[]): Session[] {
  const sessions: Session[] = records.map((r, i) => {
    const inT  = r.firstCheckIn ?? null;
    const outT = r.lastCheckOut ?? null;
    const rawStatus = (r.status || '').toString().toLowerCase();
    const status: Session['status'] =
      rawStatus === 'inside' ? 'inside'
        : (!inT && outT ? 'out' : 'left');
    const floors: string[] = Array.isArray(r.floors) ? r.floors.filter(Boolean) : (r.floors ? [r.floors] : []);
    const attendeeId = r.userId ?? r.biometricUserId ?? null;
    return {
      key:        `${attendeeId ?? 'row'}-${r.date ?? ''}-${i}`,
      attendeeId,
      date:       r.date ?? null,
      name:       r.member || r.name || '—',
      mobile:     r.userTypeLabel || (r.biometricUserId ? `ID ${r.biometricUserId}` : ''),
      branch:     r.branchName || '—',
      floors,
      floor:      floors.join(', '),
      checkIn:    inT,
      checkOut:   outT,
      durationMin: typeof r.durationMinutes === 'number' ? r.durationMinutes : null,
      status,
      sortTime:   new Date(outT || inT || 0).getTime(),
    };
  });

  return sessions.sort((a, b) => b.sortTime - a.sortTime);
}

/* ─── Columns ────────────────────────────────────────────────────────── */

const buildColumns = (hasCheckOut: boolean, onViewAttendance: (s: Session) => void) => [
  {
    title: 'Member', key: 'name', width: 210,
    render: (_: unknown, s: Session) => (
      <div className="att-member">
        <Avatar style={{ background: `${avatarColor(s.name)}22`, color: avatarColor(s.name), fontWeight: 700 }}>
          {initials(s.name)}
        </Avatar>
        <div>
          <div className="att-member-name">{s.name}</div>
          <div className="att-member-sub">{s.mobile}</div>
        </div>
      </div>
    ),
  },
  {
    title: 'Check-in', key: 'checkIn', width: 120,
    render: (_: unknown, s: Session) =>
      s.checkIn ? <span className="att-time in">↓ {fmtTime(s.checkIn)}</span> : <span className="att-time none">—</span>,
  },
  ...(hasCheckOut ? [
    {
      title: 'Check-out', key: 'checkOut', width: 120,
      render: (_: unknown, s: Session) =>
        s.checkOut ? <span className="att-time out">↑ {fmtTime(s.checkOut)}</span> : <span className="att-time none">—</span>,
    },
    {
      title: 'Duration', key: 'duration', width: 130,
      render: (_: unknown, s: Session) => {
        if (s.status === 'inside' && s.checkIn) {
          const mins = Math.max(0, dayjs().diff(dayjs(s.checkIn), 'minute'));
          return <span className="att-dur ongoing">{fmtDur(mins)} · live</span>;
        }
        return s.durationMin != null ? <strong className="att-dur">{fmtDur(s.durationMin)}</strong> : <span className="att-time none">—</span>;
      },
    },
  ] : []),
  {
    title: 'Floor', key: 'floor', width: 160,
    render: (_: unknown, s: Session) =>
      s.floor ? <span className="att-floor"><EnvironmentOutlined /> {s.floor}</span> : '—',
  },
  { title: 'Branch', key: 'branch', width: 150, render: (_: unknown, s: Session) => s.branch },
  ...(hasCheckOut ? [{
    title: 'Status', key: 'status', width: 120,
    render: (_: unknown, s: Session) => {
      if (s.status === 'inside') return <span className="att-status inside"><span className="att-dot pulse" /> Inside</span>;
      if (s.status === 'out')    return <span className="att-status out"><span className="att-dot" /> Out only</span>;
      return <span className="att-status left"><span className="att-dot" /> Left</span>;
    },
  }] : []),
  {
    title: 'Actions', key: 'actions', width: 110, fixed: 'right' as const,
    render: (_: unknown, s: Session) => (
      <Button
        size="small"
        icon={<EyeOutlined />}
        disabled={!s.attendeeId || !s.date}
        onClick={() => onViewAttendance(s)}
      >
        View
      </Button>
    ),
  },
];

/* ─── Component ──────────────────────────────────────────────────────── */

const FloorAttendance = () => {
  const navigate          = useNavigate();
  const branchId          = useSelector((state: any) => state.branch.selectedBranch);
  const selectedBranchId  = typeof branchId === 'object' ? (branchId?._id || branchId?.id) : branchId;

  const handleViewAttendance = (s: Session) => {
    if (!s.attendeeId || !s.date) return;
    navigate(`${AttendanceDetailRoute}/${s.attendeeId}?date=${s.date}`);
  };

  const [floorKey,       setFloorKey]       = useState<string | number>('all');
  const [page,           setPage]           = useState(1);
  const [limit,          setLimit]          = useState(50);
  const [search,         setSearch]         = useState('');
  const [statusTab,      setStatusTab]      = useState<string | number>('All');
  const [startDate,      setStartDate]      = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate,        setEndDate]        = useState<string>(dayjs().format('YYYY-MM-DD'));

  const activeFloor = FLOORS.find(f => f.key === floorKey) ?? FLOORS[0];
  const columns = useMemo(() => buildColumns(activeFloor.hasCheckOut, handleViewAttendance), [activeFloor.hasCheckOut]);
  const [visibleColumns, setVisibleColumns] = useState<VisibleColumns>(
    columns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as VisibleColumns)
  );

  const { data: attendanceData, isFetching } = useGetAttendanceQuery({
    userType: 'user',
    startDate,
    endDate,
    branchId: selectedBranchId || undefined,
    page,
    limit,
    search: search || undefined,
    floor: activeFloor.key,
  });

  const rawData = (attendanceData as any)?.data || [];
  const allSessions = useMemo(() => buildSessions(rawData), [rawData]);

  const dataSource = useMemo(() => {
    if (statusTab === 'Inside') return allSessions.filter(s => s.status === 'inside');
    if (statusTab === 'Left')   return allSessions.filter(s => s.status !== 'inside');
    return allSessions;
  }, [allSessions, statusTab]);

  // Server-computed KPI summary — floor-scoped too, since `floor` is sent as a query param.
  const stats = (attendanceData as any)?.summary || {};

  const handleFloorChange = (key: string | number) => {
    setFloorKey(key);
    const nextFloor = FLOORS.find(f => f.key === key) ?? FLOORS[0];
    const nextColumns = buildColumns(nextFloor.hasCheckOut, handleViewAttendance);
    setVisibleColumns(nextColumns.reduce((acc, c) => ({ ...acc, [c.key]: true }), {} as VisibleColumns));
  };

  const handleColumnToggle = (columnKey: string) =>
    setVisibleColumns(prev => ({ ...prev, [columnKey]: !prev[columnKey] }));

  const handleDateRangeChange = (range: { start: Date; end: Date }) => {
    setStartDate(dayjs(range.start).format('YYYY-MM-DD'));
    setEndDate(dayjs(range.end).format('YYYY-MM-DD'));
    setPage(1);
  };

  const handleSearch = (value: string) => { setSearch(value); setPage(1); };

  const visibleCols = useMemo(() => columns.filter(col => visibleColumns[col.key]), [columns, visibleColumns]);

  return (
    <div className="att-page floor-att-page">
      <div className="att-titlebar">
        <h2 className="att-title">Floor-wise Attendance</h2>
        <span className="att-live">● Live</span>
        <div className="att-titlebar-extra">
          <Link to={UserAttendanceRoute}>← Member Attendance</Link>
        </div>
      </div>

      <div className="floor-tabs">
        <Segmented
          value={floorKey}
          onChange={handleFloorChange}
          options={FLOORS.map(f => ({ label: f.label, value: f.key }))}
        />
        {!activeFloor.hasCheckOut && (
          <span className="floor-note">Check-in only — this floor has no out scanner</span>
        )}
      </div>

      <div className="att-stats">
        <StatCard title="Total Records"    value={stats.totalRecords}   icon={<ApartmentOutlined />}   color="#8b5cf6" subtitle="in range" />
        <StatCard title="Check-ins"        value={stats.checkIns}       icon={<LoginOutlined />}       color="#10b981" subtitle="in range" />
        {activeFloor.hasCheckOut && (
          <>
            <StatCard title="Check-outs"       value={stats.checkOuts}       icon={<LogoutOutlined />}      color="#f59e0b" subtitle="in range" />
            <StatCard title="Currently Inside" value={stats.currentlyInside} icon={<TeamOutlined />}        color="#10b981" subtitle="not checked out" />
            <StatCard title="Completed Visits" value={stats.completedVisits} icon={<CheckCircleOutlined />} color="#6366f1" subtitle="in + out" />
            <StatCard title="Avg Duration"     value={stats.avgDurationMinutes ? fmtDur(stats.avgDurationMinutes) : '—'} icon={<ClockCircleOutlined />} color="#06b6d4" subtitle="per visit" />
          </>
        )}
        <StatCard title="Members"          value={stats.uniqueMembers}  icon={<UserOutlined />}        color="#f59e0b" subtitle="unique" />
        <StatCard title="Floors Active"    value={stats.floorsActive}   icon={<EnvironmentOutlined />} color="#ec4899" subtitle="with activity" />
      </div>

      <div className="att-header">
        <div className="att-header-left">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name / mobile..." />
          <DateRangeFilter
            value={{ start: dayjs(startDate).toDate(), end: dayjs(endDate).toDate() }}
            onChange={handleDateRangeChange}
          />
          {activeFloor.hasCheckOut && (
            <Segmented value={statusTab} onChange={setStatusTab} options={['All', 'Inside', 'Left']} />
          )}
        </div>
        <div className="att-header-right">
          <ColumnVisibility columns={columns} visibleColumns={visibleColumns} onColumnToggle={handleColumnToggle} />
        </div>
      </div>

      <CommonTable
        columns={visibleCols}
        dataSource={dataSource}
        loading={isFetching}
        rowKey={(record: Session) => record.key}
        scroll={{ x: 1010 }}
        sticky={{ offsetHeader: 0 }}
        className="att-table"
      />

      <CustomPagination
        current={page}
        pageSize={limit}
        total={(attendanceData as any)?.pagination?.total || 0}
        onPageChange={(newPage: number) => setPage(newPage)}
        onPageSizeChange={(newLimit: number) => { setLimit(newLimit); setPage(1); }}
      />
    </div>
  );
};

export default FloorAttendance;
